import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkType } from '@platform/core/types';
import { useAddLinkMutation, useGetCatalogQuery, useLinksQuery, useRemoveLinkMutation } from '@platform/state/api';
import { ErrorBanner, Picker } from '@platform/ui';
import { useLabel } from '@platform/hooks/useLabel';
import { LINK_ORDER, sortLinks } from '../../tabs/related/linkOrder';
import { useDebounced } from '../../tabs/related/useDebounced';
import { useForeignRecords } from '../../tabs/related/useForeignRecords';
import { useRecordRefresh } from '../../tabs/useRecordRefresh';
import { recordCode } from '../header/recordCode';
import type { IMappingPanelProps } from './IMappingPanelProps';
import { mappingScopes } from './mappingScopes';
import { useMappingCandidates } from './useMappingCandidates';

const TONES: Record<string, string> = {
  relates: 'var(--solid-grey)', blocks: 'var(--solid-orange)', blocked: 'var(--solid-red)', parent: 'var(--solid-purple)', child: 'var(--solid-violet)',
  duplicates: 'var(--solid-amber)', duplicated: 'var(--solid-gold)', follows: 'var(--solid-green)', followed: 'var(--solid-forest)',
};

/**
 * Панель «Зіставлення» (UIUX forms/side-panels, прототип v4): «ТИП: • Пов’язано з» і «У: Усі модулі» (модулі простору — типи записів у
 * порядку прототипу; головний тип — назвою модуля); одразу — перші 30 записів
 * (основні модулі першими, «Показано 30 з N»), пошук за кодом чи назвою в усіх модулях звужує їх; результати з «+ Зв’язати»; нижче —
 * «ЗВ’ЯЗАНО · N» з пігулкою типу, «номер · назва» (без префікса типу) і × — за типом зв'язку в порядку прототипу (LINK_TYPES).
 */
export function MappingPanel({ ws, record, initialModule, onOpen }: IMappingPanelProps) {
  const { t } = useTranslation();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const { data: links = [] } = useLinksQuery(record.id);
  const [add, { error }] = useAddLinkMutation();
  const [remove] = useRemoveLinkMutation();
  const refresh = useRecordRefresh(record);
  const [type, setType] = useState<LinkType>('relates');
  const [scope, setScope] = useState(initialModule);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());
  const excluded = useMemo(() => [{ id: record.id, module: record.module, recordType: record.recordType },
    ...links.map((l) => ({ id: l.otherRecordId, module: l.otherModule, recordType: l.otherRecordType }))], [record.id, record.module, record.recordType, links]);
  const found = useMappingCandidates(ws, scope, q, excluded);
  const foreign = useForeignRecords(ws, useMemo(() => [...links.map((l) => l.otherModule), ...found.rows.map((h) => h.module)], [links, found.rows]));
  const label = useLabel();
  const scopes = useMemo(() => mappingScopes(catalog?.modules ?? []), [catalog]);
  const canEdit = record.permissions?.edit !== false;
  const k = (key: string) => `recordPage.mappingPanel.${key}`;
  return (
    <div className="rec-map">
      <div className="rec-map__top">
        <div className="rec-map__pickers">
          <Picker size="key" prefix={t(k('type'))} label={t(k('type'))} value={type} onChange={(v) => setType(v as LinkType)}
            options={LINK_ORDER.map((x) => ({ value: x, label: t(`recordPage.linkTypes.${x}`), dot: TONES[x] }))} />
          <Picker size="key" prefix={t(k('in'))} label={t(k('in'))} value={scope} onChange={setScope} placeholder={t(k('allModules'))}
            options={[{ value: '', label: t(k('allModules')) }, ...scopes.map((x) => ({ value: x.key, label: x.main ? foreign.moduleLabel(x.module) : label(x.name) }))]} />
        </div>
        <input className="rec-map__search" value={query} placeholder={t(k('search'))} aria-label={t(k('search'))} onChange={(e) => setQuery(e.target.value)} />
        <span className="rec-map__count">
          {found.loading && !found.rows.length ? t('app.loading') : found.total > found.rows.length ? t(k('showing'), { shown: found.rows.length, total: found.total }) : t(k('count'), { count: found.total })}
        </span>
        <ErrorBanner error={error} />
      </div>
      <div className="rec-map__results">
        {found.rows.map((h) => {
          const status = foreign.status(h.module, h.recordType, h.status);
          return (
            <div key={h.id} className="rec-map__row">
              <span className="rtab-dot" style={{ background: status.color }} />
              <div className="rec-map__main"><span className="rec-map__title">{h.title}</span><span className="rec-map__meta" title={h.display}>{foreign.typeLabel(h.module, h.recordType)} · {recordCode(h.display)}</span></div>
              {canEdit && <button type="button" className="rec-map__link" onClick={() => void add({ recordId: record.id, body: { toRecordId: h.id, linkType: type } }).unwrap().then(refresh).catch(() => undefined)}>{t(k('link'))}</button>}
            </div>
          );
        })}
        {!found.rows.length && <div className="rec-map__none">{found.loading ? t('app.loading') : t(k('none'))}</div>}
      </div>
      <div className="rec-map__linkedhead">{t(k('linked'), { count: links.length })}</div>
      <div className="rec-map__linked">
        {sortLinks(links).map((l) => (
          <div key={l.id} className="rec-map__chip">
            <span className="rec-map__type" style={{ background: TONES[l.linkType] ?? TONES['relates'] }}>{t(`recordPage.linkTypes.${l.linkType}`, { defaultValue: l.linkLabel })}</span>
            <button type="button" className="rec-map__open" onClick={() => onOpen(l)}>{recordCode(l.otherDisplay, l.otherCode)} · {l.otherTitle}</button>
            {canEdit && <button type="button" className="rec-map__x" title={t(k('remove'))} aria-label={t(k('remove'))}
              onClick={() => void remove({ recordId: record.id, linkId: l.id, otherId: l.otherRecordId }).unwrap().then(refresh).catch(() => undefined)}>×</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
